import { useEffect, useState } from "react";
import { useParams, Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Helmet } from "react-helmet-async";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ShieldCheck,
  MessageCircle,
  ExternalLink,
  AlertTriangle,
  MapPin,
  Tag,
  Clock
} from "lucide-react";
import { GoogleAd } from "@/components/GoogleAd";
import { formatDistanceToNow } from "date-fns";
import { ko } from "date-fns/locale";
import { PageContainer } from "@/components/layout/PageContainer";
import { QuickActions } from "@/components/chat/QuickActions";
import { ChatStartButton } from "@/components/chat/ChatStartButton";
import { useToast } from "@/hooks/use-toast";

interface ChatStartData {
  core: {
    id: number;
    type: string;
    title: string;
    description?: string;
    region?: string;
    suburb?: string;
    tags?: string[];
    created_at?: string;
  };
  safety: {
    title: string;
    guidelines: string[];
  };
  kakao: {
    url: string;
  };
  related: {
    articles: any[];
    market: any[];
    videos: any[];
    businesses: any[];
  };
  ads: any[];
}

export default function ChatStart() {
  const params = useParams<{ type: string; id: string }>();
  const { type, id } = params;
  const [clickedAd, setClickedAd] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  // Fetch chat-start data
  const { data, isLoading, error } = useQuery<ChatStartData>({
    queryKey: [`/api/chat-start/${type}/${id}`],
    enabled: !!type && !!id,
  });

  const handleKakaoClick = () => {
    if (data?.kakao.url) {
      // Log click event
      fetch(`/api/click_events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_type: type,
          content_id: parseInt(id),
          action: 'kakao_button_click',
        }),
      }).catch(console.error);

      // Open Kakao chat
      window.open(data.kakao.url, '_blank');
    }
  };

  const handleAdClick = async (adId: string, linkUrl: string) => {
    if (clickedAd.has(adId)) {
      window.open(linkUrl, '_blank');
      return;
    }

    try {
      // Log ad click
      await fetch(`/api/ads/click?id=${adId}`, {
        method: 'GET',
      });

      setClickedAd(prev => new Set(prev).add(adId));
      window.open(linkUrl, '_blank');
    } catch (error) {
      console.error('Ad click tracking failed:', error);
      window.open(linkUrl, '_blank');
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Skeleton className="h-64 w-full mb-4" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            콘텐츠를 불러오는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const { core, safety, kakao, related, ads } = data;

  // Generate SEO meta information
  const pageTitle = `${core.title} - OZK News 채팅 시작`;
  const pageDescription = core.description
    ? `${core.description.substring(0, 150)}...`
    : `${core.title}에 대해 대화를 시작하세요. OZK News 내장 채팅 기능을 통해 안전하고 편리하게 소통할 수 있습니다.`;
  const pageUrl = `https://ozknews.com/chat-start/${type}/${id}`;
  const ogImage = 'https://ozknews.com/og-image-chat.jpg'; // Default chat image

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImage} />

        {/* Additional meta tags */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />
      </Helmet>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Header: Content Info */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <Badge className="mb-2" data-testid={`badge-type-${type}`}>
                  {type === 'market' && '중고거래'}
                  {type === 'talk' && '동네톡톡'}
                  {type === 'business' && '업체정보'}
                  {type === 'article' && '뉴스'}
                  {type === 'meetup' && '모임'}
                </Badge>
                <CardTitle className="text-2xl mb-2" data-testid="text-content-title">
                  {core.title}
                </CardTitle>
                {core.description && (
                  <CardDescription className="text-base">
                    {core.description}
                  </CardDescription>
                )}
              </div>
            </div>

            {/* Meta info */}
            <div className="flex flex-wrap gap-4 mt-4 text-sm text-muted-foreground">
              {core.region && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  <span>{core.region}{core.suburb && ` · ${core.suburb}`}</span>
                </div>
              )}
              {core.created_at && (
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  <span>{formatDistanceToNow(new Date(core.created_at), { addSuffix: true, locale: ko })}</span>
                </div>
              )}
            </div>

            {/* Tags */}
            {core.tags && core.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {core.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="gap-1" data-testid={`tag-${tag}`}>
                    <Tag className="h-3 w-3" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
          </CardHeader>
        </Card>

        {/* Safety Guidelines */}
        <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950/20" data-testid="alert-safety">
          <ShieldCheck className="h-4 w-4 text-blue-600" />
          <div className="ml-2">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
              {safety.title}
            </h3>
            <ul className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
              {safety.guidelines.map((guideline, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-blue-600">•</span>
                  <span>{guideline}</span>
                </li>
              ))}
            </ul>
          </div>
        </Alert>

        {/* Local Ads */}
        {ads && ads.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">지역 광고</h3>
            <div className="grid gap-4 md:grid-cols-2" data-testid="container-local-ads">
              {ads.slice(0, 2).map((ad) => (
                <Card
                  key={ad.id}
                  className="cursor-pointer transition-transform hover:-translate-y-0.5 hover-elevate"
                  onClick={() => handleAdClick(ad.id, ad.link_url)}
                  data-testid={`card-ad-${ad.id}`}
                >
                  <CardContent className="p-0">
                    {ad.image_url && (
                      <div className="relative">
                        <img
                          src={ad.image_url}
                          alt={ad.business_name}
                          className="w-full h-48 object-cover rounded-t-xl"
                        />
                        <Badge className="absolute top-2 right-2 text-[10px] uppercase bg-gray-100/90 text-gray-700">
                          AD
                        </Badge>
                      </div>
                    )}
                    <div className="p-4">
                      <h4 className="font-medium mb-1">{ad.business_name}</h4>
                      {ad.tag && (
                        <Badge variant="outline" className="text-xs">
                          {ad.tag}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Google Ad */}
        <div className="my-6" data-testid="container-google-ad">
          <GoogleAd slot="chat-start" format="auto" />
        </div>

        {/* Phase 6.1: Quick Actions */}
        {type === "market" && (
          <QuickActions
            onPriceOffer={(price) => {
              toast({
                title: "가격 제안 전송",
                description: `$${price.toLocaleString()} 가격을 제안했습니다.`,
              });
              // TODO: 실제 채팅 메시지 전송
            }}
            onLocationSuggest={(location) => {
              toast({
                title: "거래 장소 제안",
                description: `${location}에서 거래하자고 제안했습니다.`,
              });
              // TODO: 실제 채팅 메시지 전송
            }}
            onTimeSuggest={(time) => {
              toast({
                title: "거래 시간 제안",
                description: `${time}에 거래하자고 제안했습니다.`,
              });
              // TODO: 실제 채팅 메시지 전송
            }}
          />
        )}

        {/* Phase 6.1: Primary CTA - Chat Button - 카카오톡 채팅 숨김 처리 (내장 채팅만 사용) */}
        {/* <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardContent className="py-6">
            <Button
              onClick={handleKakaoClick}
              className="w-full bg-[#FEE500] hover:bg-[#FDD835] text-[#191919] font-semibold text-lg py-6 rounded-xl"
              data-testid="button-kakao-chat"
            >
              <MessageCircle className="h-5 w-5 mr-2" />
              채팅하기
            </Button>
            <p className="text-center text-sm text-muted-foreground mt-3">
              새 창에서 카카오톡 채팅이 시작됩니다
            </p>
          </CardContent>
        </Card> */}

        {/* Related Content */}
        {(related.articles.length > 0 || related.market.length > 0 || related.videos.length > 0 || related.businesses.length > 0) && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold">관련 콘텐츠</h2>

            {/* Related Articles */}
            {related.articles.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  📰 관련 뉴스
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {related.articles.slice(0, 4).map((article: any) => (
                    <Link key={article.id} href={`/article/${article.id}`}>
                      <Card className="cursor-pointer hover-elevate transition-transform hover:-translate-y-0.5" data-testid={`card-article-${article.id}`}>
                        <CardHeader>
                          <CardTitle className="text-base line-clamp-2">
                            {article.title}
                          </CardTitle>
                          {article.created_at && (
                            <CardDescription className="text-xs">
                              {formatDistanceToNow(new Date(article.created_at), { addSuffix: true, locale: ko })}
                            </CardDescription>
                          )}
                        </CardHeader>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Market Items */}
            {related.market.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  🛒 관련 중고거래
                </h3>
                <div className="grid gap-3 md:grid-cols-3">
                  {related.market.slice(0, 6).map((item: any) => (
                    <Link key={item.id} href={`/market/${item.id}`}>
                      <Card className="cursor-pointer hover-elevate transition-transform hover:-translate-y-0.5" data-testid={`card-market-${item.id}`}>
                        {item.thumb_url && (
                          <img
                            src={item.thumb_url}
                            alt={item.title}
                            className="w-full h-32 object-cover rounded-t-xl"
                          />
                        )}
                        <CardHeader className="p-3">
                          <CardTitle className="text-sm line-clamp-2">
                            {item.title}
                          </CardTitle>
                          {item.price_cents && (
                            <Badge variant="secondary" className="w-fit">
                              A${(item.price_cents / 100).toFixed(2)}
                            </Badge>
                          )}
                        </CardHeader>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Videos */}
            {related.videos.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  🎬 관련 영상
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {related.videos.slice(0, 4).map((video: any) => (
                    <Link key={video.id} href={`/youtube/${video.id}`}>
                      <Card className="cursor-pointer hover-elevate transition-transform hover:-translate-y-0.5" data-testid={`card-video-${video.id}`}>
                        {video.thumbnail_url && (
                          <img
                            src={video.thumbnail_url}
                            alt={video.title}
                            className="w-full h-40 object-cover rounded-t-xl"
                          />
                        )}
                        <CardHeader className="p-3">
                          <CardTitle className="text-sm line-clamp-2">
                            {video.title}
                          </CardTitle>
                        </CardHeader>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Related Businesses */}
            {related.businesses.length > 0 && (
              <div className="space-y-3">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  🏢 관련 업체
                </h3>
                <div className="grid gap-3 md:grid-cols-2">
                  {related.businesses.slice(0, 6).map((business: any) => (
                    <Link key={business.id} href={`/business-directory/${business.id}`}>
                      <Card className="cursor-pointer hover-elevate transition-transform hover:-translate-y-0.5" data-testid={`card-business-${business.id}`}>
                        <CardHeader>
                          <CardTitle className="text-base">
                            {business.name}
                          </CardTitle>
                          {business.category && (
                            <Badge variant="outline" className="w-fit">
                              {business.category}
                            </Badge>
                          )}
                          {business.address && (
                            <CardDescription className="text-xs flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {business.address}
                            </CardDescription>
                          )}
                        </CardHeader>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
